import { CommonModule, ViewportScroller } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationStart,
  ResolveEnd,
  Router,
} from '@angular/router';
import { APP_SETTINGS, IAppSettings } from '@indice/ng-config';
import { TranslocoService } from '@jsverse/transloco';
import { NgProgressModule } from 'ngx-progressbar';
import { distinctUntilChanged, Observable, of, tap } from 'rxjs';
import { CoreModule } from './core/core.module';
import { CultureService } from './core/services/culture.service';
import { extractValidRoutes } from './core/services/guard-services/custom-redirect-guard.service';
import { LanguageService } from './core/services/language.service';
import { LayoutModule } from './layout/layout.module';
import { HeaderService } from './services/header.service';
import { PORTAL_API_BASE_URL } from './services/portal-api.service';
import { SeoMetaTagService } from './services/seo-meta-tags.service';
import { ModalBannerComponent } from './shared/components/banner-modal/banner-modal.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { ToasterContainerComponent } from './shared/components/toast/toast-container.component';
import { LoadingService } from './shared/services/loading.service';
import { SharedModule } from './shared/shared.module';
import { LanguageSelectorService } from './services/language-selecetro.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    CoreModule,
    LayoutModule,
    SharedModule,
    NgProgressModule,
    ToasterContainerComponent,
    LoaderComponent,
    ModalBannerComponent,
  ],
  templateUrl: './app.component.html',
  providers: [
    LoadingService,
    {
      provide: PORTAL_API_BASE_URL,
      useFactory: (appSettings: IAppSettings) => appSettings.api_url,
      deps: [APP_SETTINGS]
    }
  ],
})
export class AppComponent implements OnInit {
  title = 'Uniko Platform Portal';
  isHomePage = false;
  // Boolean to control dynamic classes for main
  siteScrolled: boolean = false;

  // Boolean to control dynamic classes for header
  public currentRoute: string = '';
  private lastRoute: string = '';
  private userLocale: string = '';
  protected showLoader = false;
  hasPreviousNavigation = false;
  showLanguagePicker= false;

  public toggleHeader$: Observable<boolean> = of(false);
  private currentLang!: string | null;
  private destroyRef = inject(DestroyRef);
  constructor(
    private router: Router,
    private _translate: TranslocoService,
    private viewportScroller: ViewportScroller,
    private _cultureService: CultureService,
    private headerService: HeaderService,
    private loadingService: LoadingService,
    private languageService: LanguageService,
    private seoMetaTagService: SeoMetaTagService,
    private languageSelectorService: LanguageSelectorService
  ) {
    this.loadingService.loading$.subscribe((loading) => {
      this.showLoader = loading;
    });
  }

  ngOnInit(): void {
    // this._translate.langChanges$.subscribe((lang) => this.languageService.setCurrentLang(lang))
    this.toggleHeader$ = this.headerService.toggleHeader$;

    // this._translate.setActiveLang(userLocale);
    let hostname = window.location.hostname;
    let hostnameParts = hostname.split('.');
    let domain = `.${hostnameParts[1]}.${hostnameParts[2]}`;
    this._cultureService.setAspCulture(this.userLocale, domain);

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        tap((routerData) => {
          if (routerData instanceof NavigationStart) {
            this.handleNavigationStart(routerData);
          }
          if (routerData instanceof NavigationEnd) {
            this.handleNavigationEnd();
          }
          if (routerData instanceof ResolveEnd) {
            this.handleResolveEnd(routerData);
          }
        }),
      )
      .subscribe();

    // # Temporary production safeguard until multilingual support is implemented. Remove when ready.
    this.showLanguagePicker =  this.languageSelectorService.getShowLanguagePicker();
  }

  private handleNavigationStart(routerData: NavigationStart) {
    // # Try to get language from localStorage
    this.setCurrentLangFromLocalStorage();

    // # If no language is found in localStorage, fall back to the browser's default language
    if (!this.currentLang) {
      this.currentLang = navigator.language === 'el-GR' ? 'el' : 'en'
    }

    // # Prioritize language selection in the following order:
    // 1. Router (URL) language
    // 2. Language saved in localStorage
    // 3. Browser's default language (also by default 'en' if browser's default language is not found)
    let languageToSet = this.getLanguageToSet(routerData.url);
    // # Temporary production safeguard until multilingual support is implemented. Remove when ready.
    if (this.showLanguagePicker == false)
    {
      languageToSet = 'el'
    }
    this._translate.setActiveLang(languageToSet);
    const validRoutes = extractValidRoutes();

    if (
      !validRoutes.includes(routerData.url) &&
      !routerData.url.includes(this.currentLang as string) &&
      routerData.url !== '/'
    ) {
      this.router.createUrlTree([routerData.url]);
    }

    if (
      validRoutes.includes(routerData.url.split('/')[2]) &&
      !routerData.url.includes(this.currentLang as string)
    ) {
      this.redirectToCurrentLang(
        `/${languageToSet}/${routerData.url.split('/')[2]}`,
      );
    }

    this.hasPreviousNavigation = Boolean(
      this.router.getCurrentNavigation()?.previousNavigation,
    );
    if (!this.hasPreviousNavigation) {
      this.loadingService.show();
    }
  }

  private setCurrentLangFromLocalStorage() {
    try {
      this.currentLang = localStorage.getItem('translocoLang');
    } catch (error) {
      console.error('Couldn`t find language in local storage');
    }
  }

  private setCurrentLangFromLanguageService() {
    this._translate.langChanges$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        this.currentLang = lang;
      });
  }

  private redirectToCurrentLang(url: string) {
    const languageToSet = this.getLanguageToSet(url);
    this.languageService.setCurrentLang(languageToSet);
    this._translate.setActiveLang(languageToSet);
    this.router.navigate([url]);
  }

  private handleNavigationEnd() {
    if (!this.hasPreviousNavigation) {
      this.loadingService.hide(true);
    }

    const currentRoute = this.router.url.split('?')[0];
    if (this.lastRoute !== currentRoute) {
      this.viewportScroller.scrollToPosition([0, 0]);
    }
    this.lastRoute = currentRoute;

    this.seoMetaTagService.SetMetaTitleByUrl(this.router.url);
    this.seoMetaTagService.SetMetaDescriptionByUrl(this.router.url);
    this.seoMetaTagService.SetMetaCanonicalLinkByUrl(this.router.url);
  }

  private handleResolveEnd(routerData: ResolveEnd) {
    this.currentRoute = routerData.url;
    this.isHomePage =
      this.currentRoute.includes('home') ||
      this.currentRoute === '/' ||
      this.currentRoute.includes('properties/');
  }

  private getLanguageToSet(url: any): string {
    const availableLanguages = this._translate.getAvailableLangs() as string[];
    let languageToSet: string;

    const routerLang = url.split('/')[1];
    if (availableLanguages.includes(routerLang)) {
      languageToSet = routerLang;
    } else if (this.currentLang && availableLanguages.includes(this.currentLang)) {
      languageToSet = this.currentLang as string;
    } else {
      languageToSet = 'el'
    }

    return languageToSet;
  }
  /**
   * @description: Function to add dynamic classes to main
   * @param: none
   * @returns: none
   * @memberof AppComponent
   */
  @HostListener('window:scroll', ['$event']) onScroll() {
    if (window.pageYOffset > 0) {
      this.siteScrolled = true;
    } else {
      this.siteScrolled = false;
    }
  }
}
